import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ShoppingBag, Search, User } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { useSettings } from "@/context/SettingsContext";
import { LOGO } from "@/data/images";
import { imgSrc } from "@/lib/img";

const HEADER_NAV = [
  { to: "/home-new", label: "Home" },
  { to: "/shop-by-category", label: "Shop by Category" },
];

export const Header = () => {
  const { cart, setDrawerOpen } = useCart();
  const { user } = useCustomerAuth();
  const s = useSettings();
  const logo = imgSrc(s.logo) || LOGO;
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <>
    <header data-testid="site-header" className="sticky top-0 z-50 bg-cream/85 backdrop-blur-xl border-b border-forest/10">
      <div className="container-nb flex items-center justify-between gap-4 py-3">
        <div className="flex items-center gap-1.5">
          <button data-testid="mobile-menu-button" onClick={() => setOpen(true)} aria-label="Menu" className="xl:hidden -ml-1 p-2.5 rounded-full text-forest hover:bg-forest/5 transition-colors">
            <Menu size={22} strokeWidth={1.8} />
          </button>
          <Link to="/" data-testid="header-logo" className="flex items-center shrink-0">
            <img src={logo} alt="Nutribest Superfoods" className="h-11 sm:h-12 w-auto mix-blend-multiply" />
          </Link>
        </div>

        <nav data-testid="desktop-nav" className="hidden xl:flex items-center gap-7">
          {HEADER_NAV.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `link-underline text-[11px] font-semibold uppercase tracking-[0.14em] text-forest/80 hover:text-forest transition-colors ${isActive ? "active text-forest" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1.5">
          <button data-testid="header-search-button" onClick={() => navigate("/faq")} aria-label="Search" className="p-2.5 rounded-full text-forest/80 hover:bg-forest/5 hover:text-forest transition-colors">
            <Search size={19} strokeWidth={1.8} />
          </button>
          <Link to={user ? "/account" : "/account/login"} data-testid="header-account-button" aria-label="Account" className="p-2.5 rounded-full text-forest/80 hover:bg-forest/5 hover:text-forest transition-colors">
            {user?.picture ? <img src={user.picture} alt="" className="w-[19px] h-[19px] rounded-full object-cover" /> : <User size={19} strokeWidth={1.8} />}
          </Link>
          <button data-testid="header-cart-button" onClick={() => setDrawerOpen(true)} aria-label="Basket" className="relative p-2.5 rounded-full text-forest/80 hover:bg-forest/5 hover:text-forest transition-colors">
            <ShoppingBag size={19} strokeWidth={1.8} />
            {cart.count > 0 && (
              <span data-testid="header-cart-count" className="absolute -top-0.5 -right-0.5 bg-gold text-white text-[10px] font-bold w-[18px] h-[18px] rounded-full flex items-center justify-center">
                {cart.count}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] text-cream flex flex-col [text-shadow:0_1px_10px_rgba(0,0,0,0.65)] bg-gradient-to-b from-forestdeep/95 via-forestdeep/55 to-forestdeep/25"
          >
            <div className="container-nb flex items-center justify-between py-4 shrink-0">
              <div className="inline-block bg-cream rounded-xl px-3 py-1.5">
                <img src={logo} alt="Nutribest Superfoods" className="h-10 w-auto mix-blend-multiply" />
              </div>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Close menu" className="p-2.5 rounded-full hover:bg-cream/10 transition-colors">
                <X size={22} />
              </button>
            </div>
            <nav className="container-nb flex-1 min-h-0 flex flex-col justify-start gap-1 overflow-y-auto overscroll-contain py-6">
              {HEADER_NAV.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.06 + i * 0.04, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
                  <NavLink
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="group flex items-baseline gap-4 py-3"
                  >
                    <span className="font-mono text-xs text-gold">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-serif text-2xl group-hover:text-goldlight group-hover:translate-x-2 transition-all duration-300">{l.label}</span>
                  </NavLink>
                </motion.div>
              ))}
              <NavLink to={user ? "/account" : "/account/login"} onClick={() => setOpen(false)} data-testid="mobile-nav-account" className="group flex items-baseline gap-4 py-3">
                <span className="font-mono text-xs text-gold"><User size={16} /></span>
                <span className="font-serif text-2xl group-hover:text-goldlight transition-all duration-300">{user ? "My Account" : "Sign in"}</span>
              </NavLink>
              <p className="pt-6 text-cream/50 text-xs tracking-[0.2em] uppercase">Pure Nutrition. Real Superfoods.</p>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
